/**
 * "Get the app": which native app a browser should be offered, where its download lives, and when the prompt shows
 * (design: https://claude.ai/artifact/Q7d4h1pAT68ob6cVoudEgk). Pure: the web gathers the user agent, display mode,
 * storage and bundle.direct's release manifest and passes them in, so this ports to Swift/Kotlin unchanged.
 *
 * Matches Slack: a slim "works better in the desktop app" strip with a download and a close, snoozed for 30 days
 * on this device; phones get an app-store style prompt, or Safari's own Smart App Banner once the App Store
 * listing is live.
 */

export type AppPlatform = "mac" | "windows" | "linux" | "ios" | "android" | "other";
/** Where the target comes from: a desktop installer, the App Store, a TestFlight beta, Google Play or the direct APK. */
export type AppStore = "desktop" | "app-store" | "testflight" | "play" | "apk";

/** What the browser says about itself (`navigator.userAgent`, `navigator.userAgentData`, `navigator.maxTouchPoints`). */
export interface UaInfo {
  userAgent: string;
  /** `navigator.userAgentData.platform` ("macOS", "Windows", "Linux", "Android", "Chrome OS"), when available. */
  uaPlatform?: string;
  /** `navigator.userAgentData.mobile`, when available. */
  uaMobile?: boolean;
  maxTouchPoints: number;
}

/** Which apps exist right now, from bundle.direct/releases/manifest.json. */
export interface Availability {
  ios: { appStoreUrl: string | null; appStoreId: string | null; testflightUrl: string | null };
  android: { playUrl: string | null; apk: boolean };
  desktop: boolean;
}

export interface AppTarget {
  platform: AppPlatform;
  store: AppStore;
  url: string;
}

export type PromptKind = "desktop" | "mobile" | "none";

/** bundle.direct, the marketing site that serves the downloads. */
export const DOWNLOAD_ORIGIN = "https://bundle.direct";
/** The release manifest the site's download page reads (public, CORS-open). */
export const MANIFEST_URL = `${DOWNLOAD_ORIGIN}/releases/manifest.json`;
/** The "Open in app" handoff page of the web app (`/app/open?to=<path>`; "app" is a reserved workspace slug). */
export const HANDOFF_PATH = "/app/open";
/** Bundle's public Google Play listing (live since 2026-10-29); a manifest `android.playUrl` overrides it. */
export const PLAY_URL = "https://play.google.com/store/apps/details?id=xyz.bigsmall.bundle";
export const SNOOZE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Nothing known yet (manifest not read, or it failed): desktop downloads still work through the redirects. */
export const NO_AVAILABILITY: Availability = {
  ios: { appStoreUrl: null, appStoreId: null, testflightUrl: null },
  android: { playUrl: PLAY_URL, apk: false },
  desktop: true,
};

// ------------------------------------------------------------------------------------------------ detection

export function detectPlatform(ua: UaInfo): AppPlatform {
  const hint = (ua.uaPlatform ?? "").toLowerCase();
  const s = ua.userAgent;
  if (hint === "android" || /Android/i.test(s)) return "android";
  if (hint === "chrome os" || hint === "chromeos" || /CrOS/.test(s)) return "other";
  if (/iPhone|iPad|iPod/.test(s)) return "ios";
  // iPadOS Safari asks for the desktop site and reports itself as a Mac; only the touch screen gives it away.
  if (hint === "macos" || /Macintosh|Mac OS X/.test(s)) return ua.maxTouchPoints > 1 ? "ios" : "mac";
  if (hint === "windows" || /Windows/.test(s)) return "windows";
  if (hint === "linux" || /Linux|X11/.test(s)) return "linux";
  return "other";
}

export function isMobilePlatform(p: AppPlatform): boolean {
  return p === "ios" || p === "android";
}

// ------------------------------------------------------------------------------------------- availability

function httpsUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return /^https:\/\/[^\s]+$/.test(value) ? value : null;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** The numeric App Store id in an apps.apple.com link (`…/app/bundle/id6825872110`). */
export function appStoreIdFromUrl(url: string | null): string | null {
  if (url === null) return null;
  return /\/id(\d{6,})(?:[/?#]|$)/.exec(url)?.[1] ?? null;
}

/** Reads bundle.direct's manifest.json (anything malformed counts as absent). */
export function availabilityFromManifest(json: unknown): Availability {
  const m = record(json);
  const ios = record(m.ios);
  const android = record(m.android);
  const desktop = record(record(m.desktop).downloads);
  const appStoreUrl = httpsUrl(ios.appStoreUrl);
  return {
    ios: { appStoreUrl, appStoreId: appStoreIdFromUrl(appStoreUrl), testflightUrl: httpsUrl(ios.testflightUrl) },
    android: { playUrl: httpsUrl(android.playUrl) ?? PLAY_URL, apk: typeof android.path === "string" && android.path !== "" },
    desktop: Object.keys(desktop).length > 0,
  };
}

// ---------------------------------------------------------------------------------------------- targets

const DESKTOP_SLUG: Partial<Record<AppPlatform, string>> = { mac: "mac", windows: "windows", linux: "linux" };

/** The site's download page, for platforms with no single target. */
export function downloadPageUrl(): string {
  return `${DOWNLOAD_ORIGIN}/download`;
}

/** The one download for this platform, or null when there's nothing to offer it. */
export function targetFor(p: AppPlatform, a: Availability): AppTarget | null {
  const slug = DESKTOP_SLUG[p];
  if (slug !== undefined) {
    // The redirect sends people to the current file, or to the download page when there is none.
    return a.desktop ? { platform: p, store: "desktop", url: `${DOWNLOAD_ORIGIN}/download/${slug}` } : null;
  }
  if (p === "ios") {
    if (a.ios.appStoreUrl !== null) return { platform: p, store: "app-store", url: a.ios.appStoreUrl };
    if (a.ios.testflightUrl !== null) return { platform: p, store: "testflight", url: a.ios.testflightUrl };
    return null;
  }
  if (p === "android") {
    if (a.android.playUrl !== null) return { platform: p, store: "play", url: a.android.playUrl };
    if (a.android.apk) return { platform: p, store: "apk", url: `${DOWNLOAD_ORIGIN}/download/android` };
    return null;
  }
  return null;
}

// ------------------------------------------------------------------------------------- open in the app

/** A same-site path ("/acme/c/general?x=1"); anything else (absolute, protocol-relative, backslashes) is not. */
export function isAppPath(path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return false;
  for (let i = 0; i < path.length; i++) if (path.charCodeAt(i) < 0x20) return false;
  return true;
}

/** The desktop app's link for an in-app path: "/acme/c/general" → "bundle://acme/c/general" (apps/desktop/INTEGRATION.md). */
export function deepLink(path: string): string {
  const safe = isAppPath(path) ? path : "/";
  return `bundle://${safe.slice(1)}`;
}

/** The handoff page for a path: "/acme/c/general" → "/app/open?to=%2Facme%2Fc%2Fgeneral". */
export function handoffUrl(path: string): string {
  return `${HANDOFF_PATH}?to=${encodeURIComponent(isAppPath(path) ? path : "/")}`;
}

/** The path the handoff page should open, from its query string; null when missing or not a same-site path. */
export function handoffTarget(search: string): string | null {
  const query = search.startsWith("?") ? search.slice(1) : search;
  for (const part of query.split("&")) {
    const eq = part.indexOf("=");
    if (eq < 0 || part.slice(0, eq) !== "to") continue;
    let value: string;
    try {
      value = decodeURIComponent(part.slice(eq + 1).replace(/\+/g, " "));
    } catch {
      return null;
    }
    return isAppPath(value) && !value.startsWith(HANDOFF_PATH) ? value : null;
  }
  return null;
}

/**
 * The `apple-itunes-app` meta content for Safari's Smart App Banner ("app-id=…, app-argument=<page URL>"), or null
 * until the App Store listing is live (the banner needs a public listing; TestFlight builds don't count).
 */
export function smartBannerContent(a: Availability, pageUrl: string): string | null {
  const id = a.ios.appStoreId;
  if (id === null) return null;
  return pageUrl.startsWith("https://") ? `app-id=${id}, app-argument=${pageUrl}` : `app-id=${id}`;
}

// ------------------------------------------------------------------------------------------------ prompt

export function snoozeUntil(nowMs: number): number {
  return nowMs + SNOOZE_DAYS * DAY_MS;
}

/** Stored snooze ("1730000000000") → ms, or null when absent or garbage. */
export function parseSnooze(value: string | null | undefined): number | null {
  if (value === null || value === undefined || !/^\d{1,16}$/.test(value)) return null;
  return Number(value);
}

/** Paths outside the signed-in app, where the prompt never shows: meetings, published pages, sign-in hand-offs, the handoff page. */
export function isPromptExcludedPath(path: string): boolean {
  return /^\/(m|s|f|invite|desktop-auth|app|auth|oauth)(\/|$|\?)/.test(path);
}

export interface PromptInput {
  platform: AppPlatform;
  /** Inside the Tauri desktop app. */
  inDesktopApp: boolean;
  /** An installed PWA (display-mode standalone / minimal-ui / fullscreen / window-controls-overlay, iOS navigator.standalone). */
  standalone: boolean;
  snoozedUntil: number | null;
  nowMs: number;
  availability: Availability;
  /** The current in-app path; meeting and published pages never show the prompt. */
  path: string;
}

/**
 * Which prompt the shell shows. iPhone with the App Store live gets Safari's Smart App Banner instead (see
 * `smartBannerContent`), so no custom prompt then.
 */
export function promptFor(i: PromptInput): { kind: PromptKind; target: AppTarget | null } {
  const none = { kind: "none" as const, target: null };
  if (i.inDesktopApp || i.standalone || isPromptExcludedPath(i.path)) return none;
  if (i.snoozedUntil !== null && i.nowMs < i.snoozedUntil) return none;
  const target = targetFor(i.platform, i.availability);
  if (target === null) return none;
  if (target.store === "app-store") return none;
  return { kind: isMobilePlatform(i.platform) ? "mobile" : "desktop", target };
}

/** Whether the profile menu offers "Get the app": everywhere but inside the desktop app. */
export function showsMenuEntry(inDesktopApp: boolean): boolean {
  return !inDesktopApp;
}

/** The menu entry's link: this platform's target, else the download page. */
export function menuTarget(p: AppPlatform, a: Availability): string {
  return targetFor(p, a)?.url ?? downloadPageUrl();
}

/** i18n keys by target (labels come from packages/i18n). */
export function downloadLabelKey(p: AppPlatform): "getApp.download.mac" | "getApp.download.windows" | "getApp.download.linux" | null {
  if (p === "mac") return "getApp.download.mac";
  if (p === "windows") return "getApp.download.windows";
  if (p === "linux") return "getApp.download.linux";
  return null;
}

export function storeLineKey(s: AppStore): "getApp.store.appStore" | "getApp.store.testflight" | "getApp.store.play" | "getApp.store.apk" | null {
  if (s === "app-store") return "getApp.store.appStore";
  if (s === "testflight") return "getApp.store.testflight";
  if (s === "play") return "getApp.store.play";
  if (s === "apk") return "getApp.store.apk";
  return null;
}

export function mobileActionKey(s: AppStore): "getApp.action.get" | "getApp.action.install" | "getApp.action.download" {
  if (s === "play") return "getApp.action.install";
  if (s === "apk") return "getApp.action.download";
  return "getApp.action.get";
}

export function menuLabelKey(p: AppPlatform): "getApp.menu.desktop" | "getApp.menu.mobile" | "getApp.menu.any" {
  if (DESKTOP_SLUG[p] !== undefined) return "getApp.menu.desktop";
  if (isMobilePlatform(p)) return "getApp.menu.mobile";
  return "getApp.menu.any";
}
